// PublicSiteLink.tsx
/**
 * "Go to <tenant> web site" (MPPI-00419).
 *
 * Both portals were one-way doors: once signed in there was no route back to
 * the public site, and the operator wanted the same affordance the login screen
 * has — visible when you arrive and when you leave.
 *
 * The label carries the TENANT's name, not the platform's, because the site it
 * opens is the tenant's. Rendering "Go to HouseKey web site" on Mina Prime's
 * admin would be the brand leak this whole sweep is about.
 *
 * Renders NOTHING when the brand has no website configured. A dead link
 * labelled with a company name is worse than no link: it implies a site exists
 * and then fails in front of the user.
 *
 * `target="_blank"` on purpose — the session stays where it is. The operator's
 * requirement was to reach the public site without being logged out, and a
 * same-tab navigation to a marketing site is how people lose their place.
 * `rel="noreferrer"` because the referrer would leak the authenticated URL.
 */
interface PublicSiteLinkProps {
  companyName: string | null | undefined;
  website: string | null | undefined;
  className?: string;
}

export function PublicSiteLink({ companyName, website, className = "" }: PublicSiteLinkProps) {
  const site = (website ?? "").trim();
  if (!site) return null;

  const href = site.startsWith("http") ? site : `https://${site}`;
  const label = (companyName ?? "").trim();

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={`Opens ${site} in a new tab`}
      className={`inline-flex items-center gap-1.5 rounded-md border border-white/25 px-2.5 py-1.5 text-xs font-medium text-white/90 transition-colors hover:border-white/50 hover:bg-white/10 hover:text-white ${className}`}
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
      </svg>
      <span className="hidden sm:inline">
        {label ? `Go to ${label} web site` : "Go to web site"}
      </span>
      <span className="sm:hidden">Web site</span>
    </a>
  );
}
